import { useCallback, useMemo, useState, type ReactNode } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { AuditLogEmptyState } from './audit-log-empty-state';
import { AuditTrailFilterPopover, formatAuditEventType } from './audit-trail-filter-popover';
import { AuditTrail } from '../../../../../backend/src/auditTrail/auditTrail.entity';
import { AuditEventType } from '../../../../../backend/src/auditTrail/auditTrail.types';
import {
  AuditTrailFilters,
  AUDIT_TRAIL_SORTABLE_COLUMNS,
} from '../../../../../backend/src/auditTrail/auditTrail.types';
import { AuditTrailDownloadColumn } from '../../../../../backend/src/auditTrail/auditTrail.types';
import { DownloadFormat } from '../../../../../backend/src/types/types';
import { useDownloadAuditTrail } from '@/api/mutations/useDownloadAuditTrail';
import { useDownloadAuditTrailArtefact } from '@/api/mutations/useDownloadAuditTrailArtefact';
import useGetAuditTrail from '@/api/queries/analyticsQueries/useGetAuditTrail';
import formatDate, { formatDateXAgo } from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { SelectFilterOption } from '@/new/components/server-side-filter-popover';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/new/components/shadcn/empty';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { ServerSideScrollableTable } from '@/new/components/table/server-side-scrollable-table';
import { cn } from '@/new/lib/utils';

// Build URL for navigating to the audit entity (returns null for deleted or non-navigable events).
// Module-level + exported so other surfaces (e.g. the Recent Activity card) link consistently.
export function getAuditEntityUrl(audit: AuditTrail): string | null {
  const detail = audit.auditDetail;
  const targetId = audit.auditTargetId;
  const mId = audit.matterId;

  switch (detail.auditEventType) {
    // Matter events - link to matter page
    case AuditEventType.MatterCreate:
    case AuditEventType.MatterEdit:
      return `/matters/${mId}`;

    // Subset events - link to subset page (targetId is subsetId)
    case AuditEventType.SubsetCreate:
    case AuditEventType.SubsetEdit:
      return `/matters/${mId}/subsets/${targetId}`;

    // Chronology events
    case AuditEventType.ChronologyCreate:
    case AuditEventType.ChronologyEdit:
      return `/matters/${mId}/subsets/${detail.subsetId}/chronologies/${targetId}`;

    // Doc Chronology events
    case AuditEventType.DocChronologyCreate:
    case AuditEventType.DocChronologyEdit:
      return `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${targetId}`;

    // Kim Thread events (old) - now deprecated so just route to the new Kim page
    case AuditEventType.ThreadCreate:
      return `/matters/${mId}/subsets/${detail.subsetId}/new-kim`;

    // Kim Thread events (new)
    case AuditEventType.KimCreateThread:
      return `/matters/${mId}/subsets/${detail.subsetId}/new-kim/${targetId}`;

    // Fact events - targetId is factId
    case AuditEventType.FactCreate:
    case AuditEventType.FactDelete:
    case AuditEventType.FactUpdate:
      return `/matters/${mId}/subsets/${detail.subsetId}/facts?factIds=${targetId}`;

    // Chronology relationship events - sourceId is factId, targetId is chronologyId
    case AuditEventType.ChronologyAddFact:
    case AuditEventType.DocChronologyAddFact:
    case AuditEventType.DocChronologyRemoveFact:
      return `/matters/${mId}/subsets/${detail.subsetId}/facts?factIds=${detail.sourceId}`;

    // Doc chronology document events - link to the doc chronology
    case AuditEventType.DocChronologyRemoveDocument:
    case AuditEventType.DocChronologyUpdateDoc:
      return `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${targetId}`;

    // VerifiedFactChronology - sourceId is the factId
    case AuditEventType.ChronologyVerifyFact:
      return `/matters/${mId}/subsets/${detail.subsetId}/facts?factIds=${detail.sourceId}`;

    // Comment events - sourceId is the factId
    case AuditEventType.CommentAdd:
    case AuditEventType.CommentUpdate:
    case AuditEventType.CommentDelete:
      return `/matters/${mId}/subsets/${detail.subsetId}/facts?factIds=${detail.sourceId}`;

    // InconsistencyReport events
    case AuditEventType.InconsistencyReportCreate:
      return `/matters/${mId}/subsets/${detail.subsetId}/reports/${targetId}`;

    // Deleted events - no link (entity no longer exists)
    case AuditEventType.MatterDelete:
    case AuditEventType.SubsetDelete:
    case AuditEventType.ChronologyDelete:
    case AuditEventType.DocChronologyDelete:
    case AuditEventType.ThreadDelete:
    case AuditEventType.KimDeleteThread:
    case AuditEventType.DocumentDelete:
    case AuditEventType.InconsistencyDelete:
    case AuditEventType.InconsistencyReportDelete:
    case AuditEventType.PersonDelete:
      return null;

    // Person/Entity events - link to entities page
    case AuditEventType.PersonUpdate:
      return `/matters/${mId}/subsets/${detail.subsetId}/entities`;

    // Procedural-timeline events all live on the matter's procedural-timeline page
    case AuditEventType.ProceduralTimelineFileUpload:
    case AuditEventType.ProceduralTimelineSuggestionAccept:
    case AuditEventType.ProceduralTimelineSuggestionReject:
    case AuditEventType.ProceduralTimelineEventCreate:
    case AuditEventType.ProceduralTimelineEventUpdate:
    case AuditEventType.ProceduralTimelineEventDelete:
    case AuditEventType.ProceduralTimelineCalendarDownload:
      return `/matters/${mId}/procedural-timeline`;

    // Other events - no direct navigation
    default:
      return null;
  }
}

export function AuditTrailTable({
  grammar,
  matterId,
  organisationId,
  userOptions,
  matterOptions,
  className,
  pageSize = 10,
}: {
  grammar: 'a' | 'this';
  // Provide exactly one scope: a matterId (single matter) or an organisationId (org-wide activity).
  // User/matter filtering for the org-wide view is handled internally via the filter popover.
  matterId?: string;
  organisationId?: string;
  // When provided, the filter popover offers user / matter dropdowns (org-wide audit view).
  userOptions?: SelectFilterOption[];
  matterOptions?: SelectFilterOption[];
  className?: string;
  pageSize?: number;
}) {
  // The org-scoped activity view is table-only; matter-scoped views keep the log/table toggle.
  const [view, setView] = useState<'log' | 'table'>('log');
  const { fetchDownload: fetchArtefactDownload } = useDownloadAuditTrailArtefact();
  const [downloadingArtefactId, setDownloadingArtefactId] = useState<string | null>(null);

  const columnHelper = createColumnHelper<AuditTrail>();

  function renderArtefactDownloadButton(audit: AuditTrail): ReactNode {
    if (!('artefact' in audit.auditDetail) || !audit.auditDetail.artefact) {
      return null;
    }

    return (
      <Button
        aria-label="Download audit artefact"
        disabled={downloadingArtefactId === audit.id}
        onClick={(event) => {
          event.stopPropagation();
          setDownloadingArtefactId(audit.id);
          fetchArtefactDownload(audit.id, { onSettled: () => setDownloadingArtefactId(null) });
        }}
        size="sm"
      >
        <Icon className="lucide--download" />
        Download Report
      </Button>
    );
  }

  // Helper to render entity name as a styled link
  function renderEntityLink(name: string, url: string | null): ReactNode {
    const nameStyle = 'rounded bg-gray-100 px-1 py-0.5 font-medium text-xs';
    if (url) {
      return (
        <Link to={url} className={`${nameStyle} text-blue-600 hover:underline`}>
          {name}
        </Link>
      );
    }
    // Fallback to styled name if no URL
    return <span className={nameStyle}>{name}</span>;
  }

  // Helper to render entity name as styled text (no link) - for deleted entities
  function renderEntityName(name: string): ReactNode {
    return <span className="rounded bg-gray-100 px-1 py-0.5 text-xs font-medium">{name}</span>;
  }

  // Helper to format camelCase field names to human-readable format
  function formatFieldName(field: string): string {
    // Handle special cases
    const specialCases: Record<string, string> = {
      // Matter fields
      isDraft: 'draft status',
      tokenLimit: 'token limit',
      disputeType: 'dispute type',
      userDefinedEntities: 'parties',
      additionalContext: 'additional context',
      timePeriod: 'time period',
      disputeStatus: 'dispute status',
      // Subset fields
      legalIssues: 'legal issues',
      // Fact fields
      factDateText: 'date',
      key: 'key status',
    };

    if (specialCases[field]) {
      return specialCases[field];
    }

    // Convert camelCase to space-separated lowercase
    return field
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .trim();
  }

  // Helper to format field values to human-readable format
  function formatFieldValue(value: string): string {
    if (!value) return value;

    // Handle special value cases
    const specialCases: Record<string, string> = {
      notRelevant: 'not relevant',
      highlyRelevant: 'highly relevant',
      isKey: 'key fact',
      notKey: 'not key',
    };

    if (specialCases[value]) {
      return specialCases[value];
    }

    return value;
  }

  // Helper to format boolean field values
  function formatBooleanValue(field: string, value: string): string {
    const isTrue = value === 'true';
    const booleanLabels: Record<string, { true: string; false: string }> = {
      key: { true: 'key', false: 'not key' },
      isDraft: { true: 'draft', false: 'not draft' },
    };
    const labels = booleanLabels[field];
    if (labels) {
      return isTrue ? labels.true : labels.false;
    }
    // Fallback for unknown boolean fields
    return isTrue ? formatFieldName(field) : `not ${formatFieldName(field)}`;
  }

  // Helper to format field change messages
  function formatFieldChange(target: string, field: string, from: string, to: string): ReactNode {
    const valueStyle = 'rounded bg-gray-100 px-1 py-0.5 font-mono text-xs';

    // Boolean fields (detected by value) use "set as" format
    const isBooleanValue = to === 'true' || to === 'false';
    if (isBooleanValue) {
      const booleanLabel = formatBooleanValue(field, to);
      return (
        <>
          set {target} as <code className={valueStyle}>{booleanLabel}</code>
        </>
      );
    }

    const readableField = formatFieldName(field);
    const readableTo = formatFieldValue(to);

    if (!from) {
      return (
        <>
          set {target} {readableField} to <code className={valueStyle}>{readableTo}</code>
        </>
      );
    }
    const readableFrom = formatFieldValue(from);
    return (
      <>
        updated {target} {readableField} from <code className={valueStyle}>{readableFrom}</code> to{' '}
        <code className={valueStyle}>{readableTo}</code>
      </>
    );
  }

  // Helper for singular/plural count with optional link
  function formatCount(count: number, singular: string, plural: string): ReactNode {
    const text = count === 1 ? `1 ${singular}` : `${count} ${plural}`;
    return text;
  }

  // Human-readable messages for each audit event type using switch for proper type narrowing
  function getAuditMessage(audit: AuditTrail): ReactNode {
    const detail = audit.auditDetail;
    const mId = audit.matterId;
    const emailStyle = 'rounded bg-gray-100 px-1 py-0.5 font-mono text-xs';
    const email = <code className={emailStyle}>{audit.user.email}</code>;
    switch (detail.auditEventType) {
      // Matter events (EntityDetail)
      case AuditEventType.MatterCreate:
        return <>{email} created this matter.</>;
      case AuditEventType.MatterEdit: {
        const matterUrl = `/matters/${mId}`;
        return (
          <>
            {email} {formatFieldChange('', detail.field, detail.from, detail.to)} for{' '}
            {renderEntityLink('matter', matterUrl)}
          </>
        );
      }
      case AuditEventType.MatterDelete:
        return (
          <>
            {email} deleted matter {renderEntityName(detail.name)}
          </>
        );

      // User events (RelationshipDetail)
      case AuditEventType.MatterAddUsers:
        return (
          <>
            {email} added {detail.sourceName} to {grammar} matter
          </>
        );
      case AuditEventType.MatterRemoveUsers:
        return (
          <>
            {email} removed {detail.sourceName} from {grammar} matter
          </>
        );

      // Issue events (RelationshipMatterDetail)
      case AuditEventType.MatterAddIssue:
        return (
          <>
            {email} added issue {renderEntityName(detail.sourceName)} to {grammar} matter
          </>
        );

      // Subset events (EntityDetail)
      case AuditEventType.SubsetCreate:
        return (
          <>
            {email} created subset {renderEntityLink(detail.name, getAuditEntityUrl(audit))}
          </>
        );
      case AuditEventType.SubsetEdit: {
        const subsetUrl = `/matters/${mId}/subsets/${audit.auditTargetId}`;
        return (
          <>
            {email} {formatFieldChange('', detail.field, detail.from, detail.to)} for{' '}
            {renderEntityLink('subset', subsetUrl)}
          </>
        );
      }
      case AuditEventType.SubsetDelete:
        return (
          <>
            {email} deleted subset {renderEntityName(detail.name)}
          </>
        );

      // Run events (CountDetail)
      case AuditEventType.RunCreate:
        return (
          <>
            {email} created a run with {formatCount(detail.count, 'document', 'documents')}
          </>
        );
      case AuditEventType.RunReprocess:
        return (
          <>
            {email} reprocessed {formatCount(detail.count, 'document', 'documents')} from a previous run
          </>
        );
      case AuditEventType.RunApprove:
        return (
          <>
            {email} approved a run ({formatCount(detail.pageCount, 'page', 'pages')})
          </>
        );
      case AuditEventType.RunReject:
        return (
          <>
            {email} cancelled a run pending approval ({formatCount(detail.pageCount, 'page', 'pages')})
          </>
        );

      // Document events (CountDetail / EntityDetail)
      case AuditEventType.DocumentUpload:
        return (
          <>
            {email} uploaded {formatCount(detail.count, 'document', 'documents')}
          </>
        );
      case AuditEventType.DocumentDelete:
        return (
          <>
            {email} deleted document {renderEntityName(detail.name)}
          </>
        );

      // Fact events (SubsetEntityDetail / UpdateFactDetail)
      case AuditEventType.FactCreate:
        return (
          <>
            {email} created a {renderEntityLink('fact', getAuditEntityUrl(audit))}
          </>
        );
      case AuditEventType.FactUpdate: {
        const factLink = renderEntityLink('fact', getAuditEntityUrl(audit));
        const valueStyle = 'rounded bg-gray-100 px-1 py-0.5 font-mono text-xs';

        // Boolean fields (detected by value) use "set as" format
        const isBooleanValue = detail.to === 'true' || detail.to === 'false';
        if (isBooleanValue) {
          const booleanLabel = formatBooleanValue(detail.field, detail.to);
          return (
            <>
              {email} set {factLink} as <code className={valueStyle}>{booleanLabel}</code>
            </>
          );
        }

        const readableField = formatFieldName(detail.field);
        const readableTo = formatFieldValue(detail.to);

        if (!detail.from) {
          return (
            <>
              {email} set {factLink} {readableField} to <code className={valueStyle}>{readableTo}</code>
            </>
          );
        }
        const readableFrom = formatFieldValue(detail.from);
        return (
          <>
            {email} updated {factLink} {readableField} from <code className={valueStyle}>{readableFrom}</code> to{' '}
            <code className={valueStyle}>{readableTo}</code>
          </>
        );
      }
      case AuditEventType.FactDelete:
        return (
          <>
            {email} archived fact {renderEntityName(detail.name)}
          </>
        );

      // Chronology events (SubsetCountDetail / SubsetEntityDetail / RelationshipDetail)
      case AuditEventType.ChronologyCreate:
        return (
          <>
            {email} created chronology {renderEntityLink(detail.name, getAuditEntityUrl(audit))} with{' '}
            {formatCount(detail.count, 'fact', 'facts')}
          </>
        );
      case AuditEventType.ChronologyDelete:
        return (
          <>
            {email} deleted chronology {renderEntityName(detail.name)}
          </>
        );
      case AuditEventType.ChronologyAddFact: {
        const factUrl = `/matters/${mId}/subsets/${detail.subsetId}/facts?factIds=${detail.sourceId}`;
        const chronologyUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/${audit.auditTargetId}`;
        return (
          <>
            {email} added a {renderEntityLink('fact', factUrl)} to chronology{' '}
            {renderEntityLink(detail.chronologyName, chronologyUrl)}
          </>
        );
      }
      case AuditEventType.ChronologyEdit: {
        const chronologyUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/${audit.auditTargetId}`;
        return (
          <>
            {email} {formatFieldChange('', detail.field, detail.from, detail.to)} for chronology{' '}
            {renderEntityLink(detail.to, chronologyUrl)}
          </>
        );
      }

      // Doc Chronology events (SubsetCountDetail / SubsetEntityDetail / RelationshipDetail)
      case AuditEventType.DocChronologyCreate:
        return (
          <>
            {email} created doc chronology {renderEntityLink(detail.name, getAuditEntityUrl(audit))} with{' '}
            {formatCount(detail.count, 'document', 'documents')}
            {detail.factCount !== undefined && <> and {formatCount(detail.factCount, 'fact', 'facts')}</>}
          </>
        );
      case AuditEventType.DocChronologyDelete:
        return (
          <>
            {email} deleted doc chronology {renderEntityName(detail.name)}
          </>
        );
      case AuditEventType.DocChronologyEdit: {
        const docChronUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${audit.auditTargetId}`;
        return (
          <>
            {email} {formatFieldChange('', detail.field, detail.from, detail.to)} for doc chronology{' '}
            {renderEntityLink(detail.to, docChronUrl)}
          </>
        );
      }
      case AuditEventType.DocChronologyUpdateDoc: {
        const docChronUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${audit.auditTargetId}`;
        const docUrl = `/matters/${mId}/subsets/${detail.subsetId}/docs?docIds=${detail.docId}`;
        return (
          <>
            {email} {formatFieldChange('', detail.field, detail.from, detail.to)} for{' '}
            {renderEntityLink('document', docUrl)} on doc chronology{' '}
            {renderEntityLink(detail.chronologyName, docChronUrl)}
          </>
        );
      }
      case AuditEventType.DocChronologyAddDocument: {
        const docChronUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${audit.auditTargetId}`;
        const docUrl = `/matters/${mId}/subsets/${detail.subsetId}/docs?docIds=${detail.sourceId}`;
        return (
          <>
            {email} added a {renderEntityLink('document', docUrl)} to doc chronology{' '}
            {renderEntityLink(detail.chronologyName, docChronUrl)}
          </>
        );
      }
      case AuditEventType.DocChronologyAddFact: {
        const factUrl = `/matters/${mId}/subsets/${detail.subsetId}/facts?factIds=${detail.sourceId}`;
        const docChronUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${audit.auditTargetId}`;
        const docUrl = `/matters/${mId}/subsets/${detail.subsetId}/docs?docIds=${detail.docId}`;
        return (
          <>
            {email} added a {renderEntityLink('fact', factUrl)} to {renderEntityLink('document', docUrl)} on doc
            chronology {renderEntityLink(detail.chronologyName, docChronUrl)}
          </>
        );
      }
      case AuditEventType.DocChronologyRemoveFact: {
        const factUrl = `/matters/${mId}/subsets/${detail.subsetId}/facts?factIds=${detail.sourceId}`;
        const docChronUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${audit.auditTargetId}`;
        const docUrl = `/matters/${mId}/subsets/${detail.subsetId}/docs?docIds=${detail.docId}`;
        return (
          <>
            {email} removed a {renderEntityLink('fact', factUrl)} from {renderEntityLink('document', docUrl)} on doc
            chronology {renderEntityLink(detail.chronologyName, docChronUrl)}
          </>
        );
      }
      case AuditEventType.DocChronologyRemoveDocument: {
        const docChronUrl = `/matters/${mId}/subsets/${detail.subsetId}/chronologies/doc/${audit.auditTargetId}`;
        const docUrl = `/matters/${mId}/subsets/${detail.subsetId}/docs?docIds=${detail.sourceId}`;
        return (
          <>
            {email} removed a {renderEntityLink('document', docUrl)} from doc chronology{' '}
            {renderEntityLink(detail.chronologyName, docChronUrl)}
          </>
        );
      }

      // Comment events (RelationshipDetail)
      case AuditEventType.CommentAdd:
        return (
          <>
            {email} added a comment to a {renderEntityLink('fact', getAuditEntityUrl(audit))}
          </>
        );
      case AuditEventType.CommentUpdate:
        return (
          <>
            {email} updated a comment on a {renderEntityLink('fact', getAuditEntityUrl(audit))}
          </>
        );
      case AuditEventType.CommentDelete:
        return (
          <>
            {email} deleted {detail.sourceOwnerName ? `${detail.sourceOwnerName}'s` : 'a'} comment on a{' '}
            {renderEntityLink('fact', getAuditEntityUrl(audit))}
          </>
        );

      // Realtime events (EntityDetail)
      case AuditEventType.RealtimeUse:
        return (
          <>
            {email} started realtime session {renderEntityName(detail.name)}
          </>
        );

      // Kim Thread events (SubsetEntityDetail)
      case AuditEventType.ThreadCreate:
        return <>{email} created a Kim thread</>;
      case AuditEventType.ThreadDelete:
        return (
          <>
            {email} deleted Kim thread {renderEntityName(detail.name)}
          </>
        );

      // Kim Thread events (new)
      case AuditEventType.KimCreateThread:
        return <>{email} created a Kim thread</>;
      case AuditEventType.KimDeleteThread:
        return (
          <>
            {email} deleted Kim thread {renderEntityName(detail.name)}
          </>
        );

      // Person/Entity events
      case AuditEventType.PersonUpdate: {
        return (
          <>
            {email} {formatFieldChange('', detail.field, detail.from, detail.to)}
          </>
        );
      }
      case AuditEventType.PersonDelete:
        return (
          <>
            {email} deleted entity {renderEntityName(detail.name)}
          </>
        );

      // Inconsistency events (EntityDetail)
      case AuditEventType.InconsistencyDelete:
        return (
          <>
            {email} deleted inconsistency {renderEntityName(detail.name)}
          </>
        );
      case AuditEventType.InconsistencyEdit: {
        const valueStyle = 'rounded bg-gray-100 px-1 py-0.5 font-mono text-xs';
        return (
          <>
            {email} changed inconsistency {formatFieldName(detail.field)} from{' '}
            <code className={valueStyle}>{formatFieldValue(detail.from)}</code> to{' '}
            <code className={valueStyle}>{formatFieldValue(detail.to)}</code>
          </>
        );
      }

      // InconsistencyReport events (SubsetEntityDetail)
      case AuditEventType.InconsistencyReportCreate:
        return (
          <>
            {email} created inconsistency report {renderEntityLink(detail.name, getAuditEntityUrl(audit))}
          </>
        );
      case AuditEventType.InconsistencyReportDelete:
        return (
          <>
            {email} deleted inconsistency report {renderEntityName(detail.name)}
          </>
        );

      // Procedural-timeline events
      case AuditEventType.ProceduralTimelineFileUpload: {
        const timelineUrl = getAuditEntityUrl(audit);
        return (
          <>
            {email} uploaded {renderEntityLink(detail.fileName, timelineUrl)} to the procedural timeline
          </>
        );
      }
      case AuditEventType.ProceduralTimelineSuggestionAccept:
      case AuditEventType.ProceduralTimelineSuggestionReject: {
        const action =
          detail.auditEventType === AuditEventType.ProceduralTimelineSuggestionAccept ? 'accepted' : 'rejected';
        const kindLabel =
          detail.kind === 'add'
            ? 'new event'
            : detail.kind === 'update'
              ? 'update'
              : detail.kind === 'delete'
                ? 'deletion'
                : detail.kind;
        return (
          <>
            {email} {action} a suggested {kindLabel} on the{' '}
            {renderEntityLink('procedural timeline', getAuditEntityUrl(audit))}
          </>
        );
      }
      case AuditEventType.ProceduralTimelineEventCreate:
        return (
          <>
            {email} created procedural event {renderEntityLink(detail.eventName, getAuditEntityUrl(audit))}
          </>
        );
      case AuditEventType.ProceduralTimelineEventUpdate:
        return (
          <>
            {email} updated procedural event {renderEntityLink(detail.eventName, getAuditEntityUrl(audit))}
          </>
        );
      case AuditEventType.ProceduralTimelineEventDelete:
        return (
          <>
            {email} deleted procedural event {renderEntityName(detail.eventName)}
          </>
        );
      case AuditEventType.ProceduralTimelineCalendarDownload:
        return (
          <>
            {email} downloaded{' '}
            {formatCount(detail.itemCount ?? 1, 'procedural timeline event', 'procedural timeline events')} as{' '}
            {detail.downloadFormat === 'zip' ? 'a calendar file archive' : 'a calendar file'} from the{' '}
            {renderEntityLink('procedural timeline', getAuditEntityUrl(audit))}
          </>
        );

      case AuditEventType.RelativitySync:
        return (
          <>
            {email} synced Relativity metadata for {formatCount(detail.filesFound, 'document', 'documents')}
          </>
        );
      case AuditEventType.RelativityBuildParentChildRelationships:
        return <>{email} built Relativity parent/child relationships</>;
      case AuditEventType.RelativityLoadFileImport:
        return (
          <>
            {email} imported load file {renderEntityName(detail.fileName)}
          </>
        );

      // Download events (DownloadDetail) — all rendered uniformly.
      case AuditEventType.DocumentDownload:
      case AuditEventType.DocChronologyDownload:
      case AuditEventType.FactDownload:
      case AuditEventType.ChronologyDownload:
      case AuditEventType.InconsistencyDownload:
      case AuditEventType.PersonDownload:
      case AuditEventType.ThreadDownload: {
        const nouns: Record<typeof detail.auditEventType, string> = {
          [AuditEventType.DocumentDownload]: 'Documents',
          [AuditEventType.DocChronologyDownload]: 'a Document Chronology',
          [AuditEventType.FactDownload]: 'Facts',
          [AuditEventType.ChronologyDownload]: 'a Chronology',
          [AuditEventType.InconsistencyDownload]: 'Inconsistencies',
          [AuditEventType.PersonDownload]: 'Entities',
          [AuditEventType.ThreadDownload]: 'a Kim Answer',
        };
        const noun = nouns[detail.auditEventType];
        const variant = detail.variant ? ` (${detail.variant})` : '';
        const format = detail.downloadFormat ? ` as ${detail.downloadFormat.toUpperCase()}` : '';
        const withDocs = detail.withSourceDocuments ? ' with source documents' : '';
        const count = detail.itemCount !== undefined ? ` — ${detail.itemCount.toLocaleString()} items` : '';
        return (
          <>
            {email} downloaded {noun}
            {variant}
            {format}
            {withDocs}
            {count}
          </>
        );
      }

      default:
        // Fallback for unknown event types
        return <>{email} performed an action</>;
    }
  }

  const columns = useMemo(
    () =>
      (view === 'table' && [
        columnHelper.accessor('user', {
          header: () => <span>User</span>,
          cell: ({ getValue }) => <span>{getValue().email}</span>,
          enableSorting: AUDIT_TRAIL_SORTABLE_COLUMNS.includes('user'),
        }),
        columnHelper.accessor('auditEventType', {
          header: () => <span>Event Type</span>,
          cell: ({ getValue }) => <span>{formatAuditEventType(getValue())}</span>,
          enableSorting: AUDIT_TRAIL_SORTABLE_COLUMNS.includes('auditEventType'),
        }),
        columnHelper.accessor('id', {
          header: () => <span>Event Message</span>,
          cell: ({ row }) => getAuditMessage(row.original),
          enableSorting: false,
        }),
        columnHelper.accessor('eventDate', {
          header: () => <span>Event Date</span>,
          cell: ({ getValue }) => <span>{formatDate(getValue(), true, true)}</span>,
          enableSorting: AUDIT_TRAIL_SORTABLE_COLUMNS.includes('eventDate'),
        }),
        columnHelper.accessor('auditDetail', {
          header: () => <span></span>,
          cell: ({ row }) => renderArtefactDownloadButton(row.original),
          enableSorting: false,
        }),
      ]) ||
      (view === 'log' && [
        columnHelper.accessor('id', {
          header: () => <span>Activity</span>,
          cell: ({ row }) => (
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-sm">{getAuditMessage(row.original)}</span>
              </div>
              <div className="flex items-center gap-4 pr-4">
                {renderArtefactDownloadButton(row.original)}
                <span className="text-xs opacity-50">{formatDateXAgo(row.original.eventDate)}</span>
              </div>
            </div>
          ),
          enableSorting: false,
        }),
      ]) ||
      [],
    [fetchArtefactDownload, downloadingArtefactId, view],
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: pageSize });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'eventDate', desc: true }]);
  const [filters, setFilters] = useState<AuditTrailFilters>({});
  const activeFilterCount =
    (filters.auditEventType?.length ?? 0) +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0) +
    (filters.userId ? 1 : 0) +
    (filters.matterId ? 1 : 0);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const debouncedSetSearchQuery = useDebouncedCallback((value: string) => {
    setDebouncedSearchQuery(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, 300);

  const onSearchQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      debouncedSetSearchQuery(query);
    },
    [debouncedSetSearchQuery],
  );
  function clearAllFilters() {
    onSearchQueryChange('');
    onFiltersChange({});
  }

  const onFiltersChange = useCallback((newFilters: AuditTrailFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const onSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    setSorting(updaterOrValue);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const { fetchDownload, isLoadingDownload } = useDownloadAuditTrail();

  const handleDownload = useCallback(() => {
    fetchDownload({
      format: DownloadFormat.CSV,
      title: 'audit-trail',
      columns: [
        AuditTrailDownloadColumn.EventDate,
        AuditTrailDownloadColumn.User,
        AuditTrailDownloadColumn.EventType,
        AuditTrailDownloadColumn.MatterName,
        AuditTrailDownloadColumn.Details,
      ],
      searchQuery: debouncedSearchQuery || undefined,
      startDate: filters.startDate,
      endDate: filters.endDate,
      matterId: matterId ?? filters.matterId,
      organisationId: organisationId,
      userId: filters.userId,
      auditEventType: filters.auditEventType,
    });
  }, [fetchDownload, debouncedSearchQuery, filters, matterId, organisationId]);

  const fallbackTableData: AuditTrail[] = [];
  const {
    data: auditTrailData,
    isLoading: isLoadingAuditTrail,
    error: auditTrailError,
  } = useGetAuditTrail({
    enabled: true,
    data: {
      ...filters,
      matterId: matterId ?? filters.matterId,
      organisationId: organisationId,
      userId: filters.userId,
      searchQuery: debouncedSearchQuery,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      paginated: true,
      skipCount: false,
      sortBy: sorting[0]?.id,
      sortDir: sorting[0]?.desc ? 'DESC' : 'ASC',
    },
  });
  const table = useReactTable({
    data: auditTrailData?.auditTrail ?? fallbackTableData,
    columns,
    state: {
      rowSelection,
      pagination,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    // Server-side pagination
    manualPagination: true,
    manualSorting: true,
    pageCount: auditTrailData?.pageCount,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: onSortingChange,
  });

  // A 403 means the user lacks the audit-view permission — show that explicitly rather than
  // an empty table, which reads as "no activity".
  if (auditTrailError?.code === 403) {
    return (
      <section className={cn('-mt-1 flex grow flex-col', className)}>
        <Empty className="py-14">
          <EmptyHeader>
            <AuditLogEmptyState />
            <EmptyTitle>You don't have permission to view the audit log</EmptyTitle>
            <EmptyDescription>
              Viewing the audit log requires additional permissions. Contact your organisation admin if you need access.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </section>
    );
  }

  return (
    <>
      <section className={cn('-mt-1 flex grow flex-col', className)}>
        <div className="mb-4 flex items-center justify-between">
          <Tabs value={view} onValueChange={(value) => setView(value as 'log' | 'table')}>
            <TabsList variant="pill">
              <TabsTrigger value="log" className="text-sm">
                <Icon className="lucide--line-chart" />
                Log
              </TabsTrigger>
              <TabsTrigger value="table" className="text-sm">
                <Icon className="lucide--table" />
                Table
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Button variant="default" onClick={handleDownload} disabled={isLoadingDownload}>
            <span className="iconify lucide--download mr-1"></span>
            {isLoadingDownload ? 'Downloading...' : 'Download Report'}
          </Button>
        </div>

        <ServerSideScrollableTable
          table={table}
          searchQuery={searchQuery}
          onSearchQueryChange={onSearchQueryChange}
          clearAllFilters={clearAllFilters}
          filterPopover={
            <AuditTrailFilterPopover
              filters={filters}
              onFiltersChange={onFiltersChange}
              clearAllFilters={clearAllFilters}
              userOptions={userOptions}
              matterOptions={matterOptions}
            />
          }
          activeFilterCount={activeFilterCount}
          plural="audit-trail"
          totalCount={Number(auditTrailData?.totalCount)}
          className="px-0"
          loading={isLoadingAuditTrail}
        />
      </section>
    </>
  );
}
